import {
  faArrowDown,
  faArrowUp,
  faTrash
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import { Controller } from 'react-hook-form';

import EPPhoneCell from './EPPhoneCell';
import EPUserPicker from './EPUserPicker';
import type { EPP1EditContentProps } from './EscalationProfile.types';

const EPP1EditContent = ({
  availableUsersForPicker,
  callingEnabled,
  callOrderUsers,
  control,
  missingPhoneUsers,
  selectedUser,
  setSelectedUser,
  onCallingToggle,
  onAddUser,
  onMoveUser,
  onRemoveUser
}: EPP1EditContentProps) => (
  <Stack spacing={1.5}>
    <Controller
      name="calling_enabled"
      control={control}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch
              checked={field.value}
              onChange={(e) => {
                field.onChange(e.target.checked);
                onCallingToggle(e.target.checked);
              }}
              sx={{ mr: 1 }}
              size="small"
            />
          }
          slotProps={{ typography: { style: { fontWeight: 'bold' } } }}
          label="Notify User via Phone Call"
        />
      )}
    />
    {callingEnabled && (
      <EPUserPicker
        options={availableUsersForPicker}
        value={selectedUser}
        onChange={setSelectedUser}
        getOptionDisabled={(u) => !u.phone_number}
        renderOptionSubtitle={(u) => (
          <Typography
            variant="caption"
            color={u.phone_number ? 'text.secondary' : 'warning'}
          >
            {u.phone_number ?? 'No phone number'}
          </Typography>
        )}
        onAdd={onAddUser}
      />
    )}
    {callingEnabled && missingPhoneUsers.length > 0 && (
      <Alert severity="warning">
        The following users are missing a phone number:{' '}
        {missingPhoneUsers.map((u) => u.name).join(', ')}
      </Alert>
    )}
    {callingEnabled && callOrderUsers.length === 0 && (
      <Typography variant="body2" color="text.secondary">
        No users configured
      </Typography>
    )}
    {callingEnabled && callOrderUsers.length > 0 && (
      <Table size="small">
        <TableBody>
          {callOrderUsers.map((user, idx) => (
            <TableRow key={user.id}>
              <TableCell sx={{ width: '2rem', py: 0.5 }}>
                <Typography sx={{ fontWeight: 'bold' }} variant="body2">
                  {idx + 1}
                </Typography>
              </TableCell>
              <TableCell sx={{ py: 0.5 }}>{user.name}</TableCell>
              <TableCell sx={{ py: 0.5 }}>{user.email}</TableCell>
              <TableCell sx={{ py: 0.5 }}>
                <EPPhoneCell user={user} />
              </TableCell>
              <TableCell sx={{ width: '8rem', py: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => onMoveUser(user.id, 'up')}
                  disabled={idx === 0}
                >
                  <FontAwesomeIcon icon={faArrowUp} size="xs" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onMoveUser(user.id, 'down')}
                  disabled={idx === callOrderUsers.length - 1}
                >
                  <FontAwesomeIcon icon={faArrowDown} size="xs" />
                </IconButton>
                <IconButton size="small" onClick={() => onRemoveUser(user.id)}>
                  <FontAwesomeIcon icon={faTrash} size="xs" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )}
  </Stack>
);

export default EPP1EditContent;
